import React, { useState } from "react";
import { ClipboardList, Hammer, Building2, Sun, BadgeCheck } from "lucide-react";
import Seo from "../components/Seo";

const FAQ = [
  ["Le bâtiment est-il vraiment offert ?", "Oui, le bâtiment séchoir (structure métallique) est offert dans le cadre d'une installation de séchage solaire Thermasol Air Systems® clé en main. Les conditions sont précisées dans l'étude gratuite."],
  ["Que dois-je réaliser moi-même ?", "Le terrain, l'étude de sol, les fondations béton avec les ancrages ou réservations conformes au plan fourni, et le raccordement électrique jusqu'au bâtiment."],
  ["Qui fournit le plan des fondations ?", "Thermasol Air Systems® fournit le plan d'implantation et les descentes de charges ; le dimensionnement des massifs est réalisé par votre maçon ou bureau d'études béton."],
  ["Le bâtiment offert est-il éligible à la prime CEE ?", "Le bâtiment permet de respecter la condition « bâtiment de séchage fermé » de la fiche AGRI-EQ-110 ; le système de séchage solaire complet neuf est éligible à la prime."],
  ["Combien de temps entre la commande et la mise en service ?", "Comptez le délai de réalisation de vos fondations (dont 28 jours de séchage du béton), puis le montage de la structure et de la toiture par nos équipes. Le planning précis est donné dans l'étude."],
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Bâtiment séchoir solaire clé en main",
    serviceType: "Étude, fourniture de la structure métallique, toiture PVT air, ventilation et mise en service",
    provider: { "@type": "Organization", name: "Thermasol Air Systems" },
    areaServed: "Europe",
    description:
      "Bâtiment séchoir solaire complet : structure métallique, toiture en panneaux PVT air Thermasol 550, ventilation et dossier CEE AGRI-EQ-110.",
  },
];

const ETAPES = [
  [ClipboardList, "Étude gratuite et plan d'implantation"],
  [Hammer, "Vous réalisez les fondations (28 jours de séchage béton)"],
  [Building2, "Montage de la structure métallique"],
  [Sun, "Pose de la toiture PVT air et du système de ventilation"],
  [BadgeCheck, "Mise en service et dossier CEE AGRI-EQ-110"],
];

const POTEAUX = [
  ["Poteau encastré", "Prévoir les réservations et couler le poteau dans 40 cm de béton, au niveau -400 mm. Les profils verticaux sont généralement livrés avec la longueur supplémentaire nécessaire."],
  ["Poteau avec pré-scellement", "Réaliser les fondations et poser les pré-scellements / tiges d'ancrage strictement selon le plan spécifique. Des chevilles chimiques peuvent être nécessaires dans certains cas, non fournies et non calculées dans la notice."],
  ["Poteau articulé sur platine", "Réaliser les massifs béton, positionner le poteau, percer et fixer les platines avec les ancrages prévus par l'étude. Ancrages Ø20 mm généralement prévus pour les poteaux, à confirmer impérativement par les descentes de charges."],
];

const H2 = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-14 mb-5">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-xl font-bold text-slate-800 mt-8 mb-3">{children}</h3>
);
const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 text-slate-700">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left px-5 py-4 font-semibold text-slate-800 hover:bg-slate-50" data-testid={`faq-batiment-${q.substring(0, 15)}`}>
        <span>{q}</span>
        <span className="text-orange-500 text-xl ml-4">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="px-5 pb-5 text-slate-600">{a}</p>}
    </div>
  );
}

export default function BatimentSechoirOffertPage() {
  return (
    <main data-testid="batiment-offert-page" className="bg-white">
      <Seo
        title="Bâtiment séchoir offert | Séchoir solaire clé en main | Thermasol Air Systems®"
        description="Pas de bâtiment pour sécher ? Thermasol Air Systems® vous offre le bâtiment séchoir avec votre installation de séchage solaire clé en main. Vous préparez le terrain et les fondations, on s'occupe du reste."
        keywords="bâtiment séchoir offert, séchoir clé en main, bâtiment de séchage clé en main, hangar séchage solaire, bâtiment agricole séchoir solaire, séchoir solaire sans bâtiment, structure métallique séchoir"
        canonical="https://thermasol-airsystems.fr/batiment-sechoir-offert/"
        ogImage="https://thermasol-airsystems.fr/images/seo/batiment.webp"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative pt-28 pb-16 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
              Bâtiment séchoir offert : votre séchoir solaire clé en main, même sans bâtiment
            </h1>
            <p className="text-slate-300 mt-6 leading-relaxed">
              Vous n'avez pas de bâtiment fermé pour installer un{" "}
              <a href="/sechoir-solaire" className="text-orange-400 font-semibold underline" data-testid="link-sechoir-batiment">séchoir solaire</a> ?
              Thermasol Air Systems® vous offre le bâtiment séchoir avec votre installation de séchage solaire clé en
              main. Structure métallique, toiture en panneaux PVT air Thermasol 550, ventilateurs, chambre d'aspiration
              et chambre de compression : nous livrons un séchoir complet, neuf et conforme à la{" "}
              <a href="/fiche-cee-agri-eq-110" className="text-orange-400 font-semibold underline" data-testid="link-cee-batiment">fiche CEE AGRI-EQ-110</a>.
              Vous n'avez qu'une chose à préparer : le terrain et les fondations.
            </p>
          </div>
          <img src="/images/seo/batiment.webp" alt="bâtiment séchoir clé en main offert avec toiture séchoir solaire Thermasol Air Systems" fetchpriority="high" className="rounded-2xl shadow-2xl w-full object-cover" width="1024" height="687" />
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
        <div className="mt-10 bg-orange-50 border-l-4 border-orange-500 rounded-r-xl p-5 text-orange-900 font-semibold" data-testid="encadre-offre">
          Bâtiment offert dans le cadre d'une installation de séchage solaire Thermasol Air Systems®. Conditions et
          surface minimale précisées dans l'étude gratuite.
        </div>

        <H2>Ce que Thermasol Air Systems® prend en charge</H2>
        <UL items={[
          "Étude de dimensionnement gratuite (tonnage, produit à sécher, ensoleillement PVGIS).",
          "Plan d'implantation et descentes de charges pour votre maçon / bureau d'études béton.",
          "Fourniture et montage de la structure métallique du bâtiment séchoir.",
          <>Toiture en <a href="/panneau-pvt-air" className="text-orange-600 font-semibold underline" data-testid="link-panneau-batiment">panneau PVT air</a> Thermasol 550.</>,
          "Ventilateurs, chambre d'aspiration, chambre de compression, gaines.",
          "Mise en service et montage du dossier prime CEE AGRI-EQ-110.",
        ]} />

        <H2>Ce qui reste à la charge de l'exploitant : le terrain et les fondations</H2>
        <p className="text-slate-700 leading-relaxed">
          La structure métallique est montée sur des supports que vous réalisez. Vous devez préparer et réaliser tout ce
          qui concerne les fondations et leur fixation.
        </p>

        <H3>1. Implantation exacte des poteaux</H3>
        <UL items={[
          "Faire reporter sur le terrain les axes et entraxes figurant sur le plan d'implantation.",
          "Respecter précisément la position de chaque poteau, y compris les poteaux de pignon, encadrements de portes et fenêtres.",
          "Le plan d'implantation et les descentes de charges doivent être remis au maçon / bureau d'études béton.",
        ]} />

        <H3>2. Étude et réalisation des fondations</H3>
        <UL items={[
          "Faire dimensionner les massifs ou longrines par un professionnel, selon les descentes de charges et la nature du sol.",
          "Réaliser terrassement, coffrage, ferraillage et béton. Le ferraillage n'est pas fourni.",
          "Contrôler le niveau de chaque massif avant la pose : le niveau des pieds de poteaux doit rester dans une tolérance de ±5 mm.",
          "Faire un relevé altimétrique et prendre le plot le plus haut comme référence.",
        ]} />

        <H3>3. Préparer le type de pose prévu sur la commande</H3>
        {/* Desktop : tableau · Mobile : cartes empilées */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="table-poteaux">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="border border-slate-300 px-4 py-3 text-left w-1/4">Type de poteau</th>
                <th className="border border-slate-300 px-4 py-3 text-left">Travail à la charge du client</th>
              </tr>
            </thead>
            <tbody>
              {POTEAUX.map(([type, txt], i) => (
                <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="border border-slate-300 px-4 py-3 font-semibold text-slate-800 align-top">{type}</td>
                  <td className="border border-slate-300 px-4 py-3 text-slate-700">{txt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-4">
          {POTEAUX.map(([type, txt], i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-5 bg-slate-50">
              <p className="font-bold text-slate-900">{type}</p>
              <p className="text-slate-700 mt-2 text-sm">{txt}</p>
            </div>
          ))}
        </div>

        <H3>4. Calage et réglage</H3>
        <UL items={[
          "Prévoir des cales métalliques ou en matériaux durables si nécessaire, jamais des cales en bois.",
          "L'épaisseur totale de calage sous une platine ne doit pas dépasser 10 mm.",
          "Les cales doivent pouvoir être entièrement enrobées par le produit de scellement si un scellement est réalisé.",
        ]} />

        <H3>5. Fixations spécifiques</H3>
        <UL items={[
          "Prévoir les chevilles / ancrages conformes et dimensionnés pour les efforts, notamment l'arrachement dû au vent.",
          "Les montants de pignon et encadrements de portes / fenêtres doivent être fixés dans un massif béton avec des ancrages Ø12 mm, sauf indication différente sur les plans.",
          "Si des tiges d'ancrage sont imposées, le détail exact vient du document de descentes de charges.",
        ]} />

        <H3>6. Après la mise en place de l'ossature</H3>
        <UL items={[
          "Le montage est réglé dans les trois axes avant serrage définitif des boulons.",
          "Pour les poteaux encastrés, le scellement final dans les 40 cm de béton se fait après réglage de l'ossature.",
          "Il est conseillé d'attendre 28 jours de séchage du béton avant de fixer les poteaux sur fondations.",
        ]} />

        <div className="mt-10 bg-slate-900 text-white rounded-2xl p-7 leading-relaxed" data-testid="encadre-recap">
          En pratique, l'exploitant livre un sol étudié, des fondations béton terminées, correctement implantées et au
          bon niveau, avec les ancrages ou réservations correspondant exactement au type de poteaux commandé. Thermasol
          Air Systems® fait le reste.
        </div>

        <H2>Les 5 étapes de votre séchoir clé en main</H2>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4" data-testid="frise-etapes">
          {ETAPES.map(([Ico, txt], i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center flex flex-col items-center">
              <span className="w-9 h-9 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center mb-2">{i + 1}</span>
              <Ico className="w-6 h-6 text-slate-700 mb-2" aria-hidden="true" />
              <p className="text-slate-700 text-sm">{txt}</p>
            </div>
          ))}
        </div>

        <H2>Pour qui ?</H2>
        <UL items={[
          "Exploitants forestiers et négociants en bois de chauffage sans hangar de séchage.",
          "Scieries et producteurs de plaquettes / granulés.",
          "Exploitations agricoles : fourrage, céréales, châtaignes, PPAM.",
          "Terrain disponible ≥ 500 m² au sol, accès camion.",
        ]} />

        <H2>Demandez votre étude gratuite</H2>
        <a href="/sechoir-solaire#etude" data-testid="cta-etude-batiment" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-full transition-colors">
          Demander mon étude gratuite
        </a>
        <p className="text-slate-600 text-sm mt-4">01 84 19 04 49 — contact@thermasol-airsystems.fr</p>

        <H2>Questions fréquentes</H2>
        <div className="space-y-3">
          {FAQ.map(([q, a]) => <FaqItem key={q} q={q} a={a} />)}
        </div>
      </div>
    </main>
  );
}
